import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  SimpleChanges,
  Injector,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  NgControl,
  FormsModule,
} from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { CheckBoxModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';

export interface CheckboxOption {
  id: string;
  label: string;
  value: any;
  checked: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
}

export interface CheckboxConfig {
  options?: CheckboxOption[];
  multiple?: boolean;
  disabled?: boolean;
  labelPosition?: 'before' | 'after';
  size?: 'small' | 'medium' | 'large';
  rounded?: 'small' | 'medium' | 'large' | 'full';
  layout?: 'horizontal' | 'vertical';
  showSelectAllControls?: boolean;
  cssClass?: string;
  required?: boolean;
  errorMessage?: string;
  showErrors?: boolean;
  hint?: string;
}

@Component({
  selector: 'app-checkbox',
  standalone: true,
  imports: [CommonModule, FormsModule, CheckBoxModule, ButtonsModule, LabelModule],
  templateUrl: './checkbox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
})
export class CheckboxComponent implements ControlValueAccessor, Validator, OnInit, OnChanges {
  @Input() config?: CheckboxConfig;

  options: CheckboxOption[] = [];
  multiple: boolean = true;
  disabled: boolean = false;
  labelPosition: 'before' | 'after' = 'after';
  size: 'small' | 'medium' | 'large' = 'medium';
  rounded: 'small' | 'medium' | 'large' | 'full' = 'medium';
  layout: 'horizontal' | 'vertical' = 'vertical';
  showSelectAllControls: boolean = true;
  cssClass: string = '';
  required: boolean = false;
  errorMessage: string = '';
  showErrors: boolean = true;
  hint: string = '';

  @Output() selectionChange = new EventEmitter<CheckboxOption[]>();
  @Output() optionChange = new EventEmitter<{
    option: CheckboxOption;
    checked: boolean;
  }>();
  @Output() selectAllChange = new EventEmitter<boolean>();

  selectedValues: any[] = [];
  selectAllState: boolean = false;
  selectAllIndeterminate: boolean = false;

  // Validation properties
  touched: boolean = false;
  errors: ValidationErrors | null = null;
  public ngControl: NgControl | null = null;

  private isInternalChange = false;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly injector: Injector
  ) {}

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
    this.applyConfig();
    this.updateSelectedValues();
    this.updateSelectAllState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.applyConfig();
      this.updateSelectAllState();
    }
  }

  private applyConfig(): void {
    if (this.config) {
      this.options = this.config.options ?? this.options;
      this.multiple = this.config.multiple ?? this.multiple;
      this.disabled = this.config.disabled ?? this.disabled;
      this.labelPosition = this.config.labelPosition ?? this.labelPosition;
      this.size = this.config.size ?? this.size;
      this.rounded = this.config.rounded ?? this.rounded;
      this.layout = this.config.layout ?? this.layout;
      this.showSelectAllControls = this.config.showSelectAllControls ?? this.showSelectAllControls;
      this.cssClass = this.config.cssClass ?? this.cssClass;
      this.required = this.config.required ?? this.required;
      this.errorMessage = this.config.errorMessage ?? this.errorMessage;
      this.showErrors = this.config.showErrors ?? this.showErrors;
      this.hint = this.config.hint ?? this.hint;
    }
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};

    if (this.required) {
      const value = control.value;

      if (this.multiple) {
        // For multiple, need at least one selected
        if (!value || !Array.isArray(value) || value.length === 0) {
          errors['required'] = true;
        }
      } else if (!value || (Array.isArray(value) && value.length === 0)) {
        errors['required'] = true;
      }
    }

    this.errors = Object.keys(errors).length > 0 ? errors : null;
    return this.errors;
  }

  // Validation display getters
  get showErrorState(): boolean {
    if (!this.showErrors) return false;

    const hasErrors = !!(this.errors || this.ngControl?.control?.errors);
    if (!hasErrors) return false;

    const isTouched =
      this.touched || this.ngControl?.control?.touched || this.ngControl?.control?.dirty;

    return !!isTouched;
  }

  get currentErrorMessage(): string {
    if (!this.showErrorState) return '';

    const errors = this.ngControl?.control?.errors || this.errors;
    if (!errors) return '';

    if (errors['required']) {
      return this.errorMessage || 'Please select at least one option';
    }

    return this.errorMessage || 'Invalid selection';
  }

  onCheckboxChange(option: CheckboxOption): void {
    this.isInternalChange = true;
    this.touched = true;

    option.checked = !option.checked;

    if (!this.multiple) {
      this.options.forEach((opt) => {
        if (opt.id !== option.id) {
          opt.checked = false;
        }
      });
    }

    this.updateSelectedValues();
    this.updateSelectAllState();
    const newValue = this.getSelectedValues();
    this.onChange(newValue);
    this.onTouched();

    this.optionChange.emit({ option, checked: option.checked });

    // Trigger validation
    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }

    setTimeout(() => {
      this.isInternalChange = false;
    }, 0);

    this.cdr.markForCheck();
  }

  onSelectAllChange(): void {
    this.touched = true;
    this.selectAllState = !this.selectAllState;

    this.options.forEach((opt) => {
      if (!opt.disabled) {
        opt.checked = this.selectAllState;
      }
    });

    this.selectAllIndeterminate = false;
    this.updateSelectedValues();
    this.selectAllChange.emit(this.selectAllState);

    const newValue = this.getSelectedValues();
    this.onChange(newValue);
    this.onTouched();

    // Trigger validation
    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }

    this.cdr.markForCheck();
  }

  private updateSelectAllState(): void {
    const enabledOptions = this.options.filter((opt) => !opt.disabled);
    const checkedCount = enabledOptions.filter((opt) => opt.checked).length;

    if (checkedCount === 0) {
      this.selectAllState = false;
      this.selectAllIndeterminate = false;
    } else if (checkedCount === enabledOptions.length) {
      this.selectAllState = true;
      this.selectAllIndeterminate = false;
    } else {
      this.selectAllState = false;
      this.selectAllIndeterminate = true;
    }
  }

  private updateSelectedValues(): void {
    const selected = this.options.filter((opt) => opt.checked);
    this.selectedValues = selected.map((opt) => opt.value);
    this.selectionChange.emit(selected);
  }

  getSelectedValues(): any {
    const selected = this.options.filter((opt) => opt.checked);
    if (this.multiple) {
      return selected.map((opt) => opt.value);
    } else {
      return selected.length > 0 ? selected[0].value : null;
    }
  }

  getSelectedOptions(): CheckboxOption[] {
    return this.options.filter((opt) => opt.checked);
  }

  writeValue(value: any): void {
    if (this.isInternalChange) {
      return;
    }

    if (value !== null && value !== undefined && value !== '') {
      const values = Array.isArray(value) ? value : [value];

      this.options.forEach((option) => {
        const shouldBeChecked = values.includes(option.value);
        if (option.checked !== shouldBeChecked) {
          option.checked = shouldBeChecked;
        }
      });

      this.updateSelectedValues();
      this.updateSelectAllState();
      this.cdr.markForCheck();
    } else {
      this.options.forEach((option) => {
        if (option.checked !== false) {
          option.checked = false;
        }
      });
      this.updateSelectedValues();
      this.updateSelectAllState();
      this.cdr.markForCheck();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  isIndeterminate(): boolean {
    return this.selectAllIndeterminate;
  }

  isAllSelected(): boolean {
    return this.selectAllState && !this.selectAllIndeterminate;
  }

  getCheckedCount(): number {
    return this.options.filter((opt) => opt.checked).length;
  }

  getTotalCount(): number {
    return this.options.filter((opt) => !opt.disabled).length;
  }

  trackByOption(index: number, option: CheckboxOption): string {
    return option.id;
  }
}
